import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchAuthSession, signOut, getCurrentUser } from 'aws-amplify/auth';
import axios from 'axios';
import { API_URL } from '../runtimeConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userAttributes, setUserAttributes] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens) {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        setIsAuthenticated(true);
        
        // Extract user attributes from ID token
        const idToken = session.tokens.idToken;
        if (idToken) {
          const cognitoGroups = idToken.payload['cognito:groups'] || [];

          // role/projectAccess/tiles are resolved server-side (single source
          // of truth — see server/services/entitlements.js) rather than
          // re-derived here, so client and server can never disagree.
          let entitlements = { role: 'viewer', projectAccess: [], tiles: [] };
          try {
            const res = await axios.get(`${API_URL}/api/me/entitlements`, {
              headers: { Authorization: `Bearer ${idToken.toString()}` },
            });
            if (res.data.success) {
              entitlements = { role: res.data.role, projectAccess: res.data.projectAccess, tiles: res.data.tiles };
            }
          } catch (err) {
            console.error('Failed to fetch entitlements:', err.message);
            // fail closed — no tiles/project access rather than trusting a stale local guess
          }

          setUserAttributes({
            email: idToken.payload.email,
            sub: idToken.payload.sub,
            username: idToken.payload['cognito:username'],
            groups: cognitoGroups,
            projectAccess: entitlements.projectAccess,
            role: entitlements.role,
            tiles: entitlements.tiles,
          });
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setUserAttributes(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      setUserAttributes(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
      setUserAttributes(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const hasProjectAccess = (projectId) => {
    if (!userAttributes || !userAttributes.projectAccess) return false;
    return userAttributes.projectAccess.includes(projectId) || 
           userAttributes.role === 'admin';
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    userAttributes,
    checkAuthStatus,
    logout,
    hasProjectAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
